import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { ApiError } from '../api/client'
import { runReport } from '../api/reports'
import { ALL_VIEWS, PERIOD_LABELS, sanitize, VIEW_LABELS } from './describe'
import { ResultView } from './ResultView'
import { DrillPanel } from './DrillPanel'
import { useDrill } from './useDrill'
import type { CatalogSet, Definition, Filter, Locale, View, Words } from './types'

/**
 * RAPORTUL DIN PAGINA: mai multe blocuri, fiecare cu definitia lui, rulate
 * fiecare cu aceleasi limite ca previzualizarea. Traieste numai in memorie -
 * nu exista inca tabele pentru rapoarte, deci nici salvare. Un bloc se poate
 * muta, lati, schimba ca vedere sau scoate; restul raportului nu se atinge.
 *
 * Un filtru "contine" pe un camp de text (imprimanta, utilizator, aplicatie) se
 * arata ca o casuta in bloc, ca sa se poata scrie numele cautat fara sa se
 * refaca blocul: sablonul "Unde e instalata imprimanta" vine cu casuta goala,
 * iar o casuta goala nu filtreaza (`sanitize`).
 *
 * `readOnly` e raportul salvat DESCHIS (nu in lucru): blocurile se arata asa
 * cum au fost salvate, cu perioada lor, fara nicio unealta de schimbare; un
 * filtru "contine" se spune ca text (cel gol nu se arata), nu ca o casuta.
 */

export interface Block {
  id: number
  title: Words
  definition: Definition
  wide: boolean
}

const REASONS: Record<string, Words> = {
  no_access: { ro: 'Nu ai acces la aceste date.', en: 'You have no access to this data.' },
  module_inactive: { ro: 'Modulul acestor date nu e activ pe companie.', en: 'The module behind this data is not active for the company.' },
}

/** Perioada blocului, spusa scurt; nimic cand blocul nu are perioada. */
function periodText(def: Definition, locale: Locale): string {
  const p = def.period
  if (!p || p.key === 'all') return ''
  if (p.key === 'custom') return `${p.from} - ${p.to}`
  if (p.key === 'last_n_days') return locale === 'ro' ? `ultimele ${p.n} zile` : `last ${p.n} days`
  return PERIOD_LABELS[p.key]?.[locale] ?? ''
}

/**
 * Casuta unui filtru "contine": scrie in definitie la Enter sau cand iese din
 * ea. Cheia poarta valoarea, deci o valoare schimbata din afara o reface.
 */
function FilterBox({ label, value, locale, onCommit }: { label: string; value: string; locale: Locale; onCommit: (v: string) => void }) {
  const [text, setText] = useState(value)
  const commit = () => {
    const v = text.trim()
    if (v !== value) onCommit(v)
  }
  return (
    <label className="od-rep-block-filter">
      <span>
        {label} {locale === 'ro' ? 'contine' : 'contains'}
      </span>
      <input
        type="search"
        className="form-control input-sm"
        value={text}
        placeholder={locale === 'ro' ? 'scrie un nume…' : 'type a name…'}
        onChange={(e) => setText(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') commit()
        }}
      />
    </label>
  )
}

/**
 * Filtrele care se pot scrie direct in bloc: "contine" pe un camp de text. Un
 * camp personal (utilizatorul) fara `reports.sensitive` nu primeste casuta:
 * serverul ar refuza filtrul (`no_access_field`), ca sa nu se ghiceasca prin
 * eliminare ce e mascat.
 */
function textFilters(def: Definition, sets: CatalogSet[], sensitive: boolean): { index: number; filter: Filter; label: Words }[] {
  const set = sets.find((s) => s.key === def.dataset)
  if (!set) return []
  const out: { index: number; filter: Filter; label: Words }[] = []
  ;(def.filters ?? []).forEach((filter, index) => {
    const field = set.fields.find((f) => f.key === filter.field)
    if (field && field.type === 'text' && (sensitive || field.sensitivity !== 'personal') && filter.op === 'contains' && typeof (filter.value ?? '') === 'string') out.push({ index, filter, label: field.label })
  })
  return out
}

function BlockView({ block, sets, sensitive, company, locale, first, last, readOnly, onChange, onRemove, onMove }: {
  block: Block
  sets: CatalogSet[]
  sensitive: boolean
  company: number
  locale: Locale
  readOnly: boolean
  first: boolean
  last: boolean
  onChange: (b: Block) => void
  onRemove: () => void
  onMove: (dir: -1 | 1) => void
}) {
  const runnable = sanitize(block.definition)
  const result = useQuery({
    queryKey: ['reports', 'run', company, JSON.stringify(runnable)],
    queryFn: () => runReport(company, runnable),
    enabled: company > 0,
    retry: false,
    staleTime: 30 * 1000,
  })
  const drill = useDrill(runnable, sets, result.data, JSON.stringify(runnable), locale)
  const t = (ro: string, en: string) => (locale === 'ro' ? ro : en)
  const error = result.error instanceof ApiError ? result.error : null
  const reason = error?.details?.error ? String(error.details.error) : ''
  const period = periodText(block.definition, locale)
  const boxes = textFilters(block.definition, sets, sensitive)
  const setFilter = (index: number, value: string) => {
    const filters = (block.definition.filters ?? []).map((f, i) => (i === index ? { ...f, value } : f))
    onChange({ ...block, definition: { ...block.definition, filters } })
  }

  return (
    <div className={`od-rep-block${block.wide ? ' od-rep-block-wide' : ''}`}>
      <div className="od-rep-block-head">
        <h4>{block.title[locale]}</h4>
        {readOnly ? (
          period ? <small className="od-rep-block-period">{period}</small> : null
        ) : (
        <span>
          <select
            className="od-rep-chip-select"
            value={block.definition.view ?? 'table'}
            aria-label={t('Vedere', 'View')}
            onChange={(e) => onChange({ ...block, definition: { ...block.definition, view: e.target.value as View } })}
          >
            {ALL_VIEWS.map((v) => (
              <option key={v} value={v}>
                {VIEW_LABELS[v][locale]}
              </option>
            ))}
          </select>
          <button type="button" className="od-rep-x" title={t('Lat / ingust', 'Wide / narrow')} onClick={() => onChange({ ...block, wide: !block.wide })}>
            ⇔
          </button>
          <button type="button" className="od-rep-x" disabled={first} aria-label={t('Muta mai sus', 'Move up')} onClick={() => onMove(-1)}>
            ↑
          </button>
          <button type="button" className="od-rep-x" disabled={last} aria-label={t('Muta mai jos', 'Move down')} onClick={() => onMove(1)}>
            ↓
          </button>
          <button type="button" className="od-rep-x" aria-label={t('Scoate blocul', 'Remove block')} onClick={onRemove}>
            ×
          </button>
        </span>
        )}
      </div>
      {readOnly ? (
        boxes.some((b) => String(b.filter.value ?? '') !== '') ? (
          <div className="od-rep-block-filters">
            {boxes
              .filter((b) => String(b.filter.value ?? '') !== '')
              .map((b) => (
                <span key={b.index} className="od-rep-block-filter od-rep-block-filter-ro">
                  {b.label[locale]} {locale === 'ro' ? 'contine' : 'contains'} <b>{String(b.filter.value)}</b>
                </span>
              ))}
          </div>
        ) : null
      ) : boxes.length > 0 ? (
        <div className="od-rep-block-filters">
          {boxes.map((b) => (
            <FilterBox key={`${b.index}:${String(b.filter.value ?? '')}`} label={b.label[locale]} value={String(b.filter.value ?? '')} locale={locale} onCommit={(v) => setFilter(b.index, v)} />
          ))}
        </div>
      ) : null}
      {result.isFetching && !result.data ? <div className="od-rep-hint">{t('Se incarca…', 'Loading…')}</div> : null}
      {error ? <div className="od-rep-hint">{REASONS[reason]?.[locale] ?? error.message}</div> : null}
      {result.data ? <ResultView result={result.data} locale={locale} onDrill={drill.onDrill} /> : null}
      {drill.drill ? <DrillPanel company={company} drill={drill.drill} locale={locale} onClose={drill.close} /> : null}
    </div>
  )
}

export function ReportBlocks({ blocks, sets, sensitive, company, locale, readOnly = false, onChange }: { blocks: Block[]; sets: CatalogSet[]; sensitive: boolean; company: number; locale: Locale; readOnly?: boolean; onChange: (blocks: Block[]) => void }) {
  if (blocks.length === 0) return null
  const replace = (i: number, b: Block) => onChange(blocks.map((x, j) => (j === i ? b : x)))
  const move = (i: number, dir: -1 | 1) => {
    const next = [...blocks]
    const [b] = next.splice(i, 1)
    next.splice(i + dir, 0, b!)
    onChange(next)
  }
  return (
    <div className={`od-rep-blocks${readOnly ? ' od-rep-blocks-readonly' : ''}`}>
      {blocks.map((b, i) => (
        <BlockView
          key={b.id}
          block={b}
          sets={sets}
          sensitive={sensitive}
          company={company}
          locale={locale}
          first={i === 0}
          last={i === blocks.length - 1}
          readOnly={readOnly}
          onChange={(nb) => replace(i, nb)}
          onRemove={() => onChange(blocks.filter((x) => x.id !== b.id))}
          onMove={(dir) => move(i, dir)}
        />
      ))}
    </div>
  )
}
